import { DateTime } from 'luxon';

import { BillTiming, PaymentFrequency } from '@willow/types-iso';

import { getNextPaymentDueDate } from './getNextPaymentDueDate';
import { getStatementDateForPaymentDue } from './getStatementDateForPaymentDue';

// Runaway guard: no real schedule reaches 100 years of payments, so hitting this means the inputs are bad.
const MAX_AMORTIZATION_MONTHS = 1200;

export interface MissingStatementPeriod {
  amortizationMonth: number;
  paymentDate: Date;
}

interface GetMissingStatementPeriodsInput {
  firstDueDate: Date;
  /** Willow doesn't service payments before this date, so it never generates statements for them. */
  firstCollectedPaymentDate: Date;
  paymentFrequency: PaymentFrequency;
  /** Payment date of the newest existing payment due, or undefined when the loan has none. */
  lastPaymentDueDate: Date | undefined;
  today: Date;
  billTiming?: BillTiming;
}

/**
 * The amortization months that fall after the newest existing payment due and whose statement date has
 * already passed — i.e. the statements a loan should already have but doesn't.
 *
 * Bounded by the statement date rather than the payment date because statements are generated ahead of
 * the payment they bill for; this matches isOnOrAfterStatementGenerationDate in shared-node.
 *
 * Periods before firstCollectedPaymentDate are excluded, mirroring the guard in
 * handleGenerateNextStatement — otherwise an aged loan would propose statements it would then refuse.
 *
 * Monthly loans only — annual schedules are out of scope.
 */
export const getMissingStatementPeriods = ({
  firstDueDate,
  firstCollectedPaymentDate,
  paymentFrequency,
  lastPaymentDueDate,
  today,
  billTiming,
}: GetMissingStatementPeriodsInput): MissingStatementPeriod[] => {
  if (paymentFrequency !== 12) {
    return [];
  }

  const endOfToday = DateTime.fromJSDate(today).endOf('day');
  const lastExisting = lastPaymentDueDate ? DateTime.fromJSDate(lastPaymentDueDate).startOf('day') : undefined;
  const firstCollected = DateTime.fromJSDate(firstCollectedPaymentDate).startOf('day');

  const periods: MissingStatementPeriod[] = [];
  for (let amortizationMonth = 1; amortizationMonth <= MAX_AMORTIZATION_MONTHS; amortizationMonth += 1) {
    const paymentDate = getNextPaymentDueDate(firstDueDate, amortizationMonth, paymentFrequency);
    const statementDate = DateTime.fromJSDate(getStatementDateForPaymentDue(paymentDate, billTiming)).startOf('day');

    if (statementDate > endOfToday) {
      break;
    }
    const startOfPaymentDate = DateTime.fromJSDate(paymentDate).startOf('day');
    if (startOfPaymentDate < firstCollected) {
      continue;
    }
    if (lastExisting && startOfPaymentDate <= lastExisting) {
      continue;
    }
    periods.push({ amortizationMonth, paymentDate });
  }

  return periods;
};
